import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, MessageSquare, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cancelInvitation } from '@/services/api.client';
import type { Invitation } from '@/types';
import { formatDateTime, statusBadgeClass } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  invitation: Invitation | null;
  estateName: string | undefined;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-[var(--border-subtle)] last:border-0">
      <span className="text-xs text-[var(--text-tertiary)] uppercase tracking-wide">{label}</span>
      <span className="text-sm text-[var(--text-primary)] text-right break-words max-w-[60%]">
        {value}
      </span>
    </div>
  );
}

function rsvpLabel(rsvp: Invitation['rsvpStatus']): string {
  if (rsvp === 'confirmed') return 'Confirmed';
  if (rsvp === 'declined') return 'Declined';
  return 'Awaiting reply';
}

export function InvitationDetailSheet({
  open,
  onOpenChange,
  propertyId,
  invitation,
  estateName,
}: Props) {
  const qc = useQueryClient();

  const cancelMut = useMutation({
    mutationFn: (id: string) => cancelInvitation(propertyId, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invitations'] });
      toast.success('Invitation cancelled');
      onOpenChange(false);
    },
    onError: () => toast.error('Could not cancel. Please try again.'),
  });

  // All hooks MUST be declared before any early return — React rules of hooks.
  const [manualCopyText, setManualCopyText] = useState<string | null>(null);

  if (!invitation) return null;

  const isPendingReg = invitation.status === 'awaiting_visitor_registration';

  const inviteUrl = invitation.qrTokenRef
    ? `${window.location.origin}/invitation/${invitation.qrTokenRef}`
    : null;

  const shareMessage =
    inviteUrl && estateName
      ? isPendingReg
        ? `Hi ${invitation.visitorName}, you've been invited to ${estateName} on ${formatDateTime(
            invitation.validFrom,
          )}. Please register your visit using this link: ${inviteUrl}`
        : `Hi ${invitation.visitorName}, you're invited to ${estateName} on ${formatDateTime(
            invitation.validFrom,
          )}. Open this link at the gate: ${inviteUrl}${
            invitation.pin ? `\nPIN: ${invitation.pin}` : ''
          }`
      : null;

  const isTerminal =
    invitation.status === 'used' ||
    invitation.status === 'expired' ||
    invitation.status === 'cancelled' ||
    invitation.status === 'declined';

  const copy = async (text: string, label: string) => {
    // Primary: Clipboard API (requires secure context + document focus)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
        setManualCopyText(null);
        return;
      } catch {
        // fall through to execCommand
      }
    }
    // Fallback: execCommand (works in Sheet context where focus may be shifted)
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) {
        toast.success(`${label} copied to clipboard`);
        setManualCopyText(null);
        return;
      }
    } catch {
      // fall through
    }
    // Last resort: show text in a selectable input
    setManualCopyText(text);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{invitation.visitorName}</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 px-4 pb-6">
          {/* Status row */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={statusBadgeClass(invitation.status)}>
              {invitation.status === 'awaiting_visitor_registration'
                ? 'Awaiting registration'
                : invitation.status.toUpperCase()}
            </Badge>
            {!isPendingReg && (
              <Badge variant="outline" className="text-xs">
                RSVP: {rsvpLabel(invitation.rsvpStatus)}
              </Badge>
            )}
          </div>

          {/* Pre-registration info banner */}
          {isPendingReg && (
            <div
              className="rounded-lg border px-3 py-2.5 text-xs"
              style={{
                borderColor: 'var(--status-amber)',
                background: 'var(--status-amber-tint)',
                color: 'var(--status-amber-text)',
              }}
            >
              <p className="font-medium mb-0.5">Waiting for visitor to register</p>
              <p>
                {invitation.visitorName} needs to open the link below and confirm their details
                before their QR code is issued. Share it with them if they haven&apos;t received it
                yet.
              </p>
            </div>
          )}

          {/* Details */}
          <div>
            {invitation.visitorPhone && <Row label="Phone" value={invitation.visitorPhone} />}
            {invitation.visitorEmail && <Row label="Email" value={invitation.visitorEmail} />}
            <Row
              label="Valid window"
              value={
                <>
                  {formatDateTime(invitation.validFrom)}
                  <br />
                  to {formatDateTime(invitation.validUntil)}
                </>
              }
            />
            {invitation.gateName && <Row label="Gate" value={invitation.gateName} />}
            {invitation.notes && <Row label="Notes" value={invitation.notes} />}
            {invitation.pin && (
              <Row label="PIN" value={<span className="font-mono">{invitation.pin}</span>} />
            )}
          </div>

          {/* Share actions — only when invitation is still live */}
          {!isTerminal && shareMessage && inviteUrl && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">
                {isPendingReg ? 'Send registration link' : 'Share with your visitor'}
              </p>
              <Button onClick={() => void copy(shareMessage, 'Share message')} className="w-full">
                <MessageSquare className="size-4 mr-2" />
                Copy share message
              </Button>
              <Button
                variant="outline"
                onClick={() => void copy(inviteUrl, 'Link')}
                className="w-full"
              >
                <Copy className="size-4 mr-2" />
                Copy link only
              </Button>
              {/* Manual copy fallback — shown when clipboard API and execCommand both fail */}
              {manualCopyText !== null && (
                <div className="space-y-1">
                  <p className="text-xs text-[var(--text-secondary)]">
                    Clipboard access blocked. Select and copy the text below:
                  </p>
                  <input
                    readOnly
                    value={manualCopyText}
                    onFocus={(e) => e.currentTarget.select()}
                    onClick={(e) => (e.currentTarget as HTMLInputElement).select()}
                    className="w-full rounded border border-[var(--border-default)] bg-[var(--bg-surface-raised)] px-3 py-2 text-xs font-mono text-[var(--text-primary)]"
                    tabIndex={-1}
                    aria-label="Text to copy manually"
                  />
                </div>
              )}
            </div>
          )}

          {/* Cancel — only when still active */}
          {!isTerminal && (
            <div className="pt-2 border-t border-[var(--border-subtle)]">
              <Button
                variant="outline"
                onClick={() => cancelMut.mutate(invitation.id)}
                disabled={cancelMut.isPending}
                className="w-full text-destructive border-destructive hover:bg-[var(--status-red-tint)]"
              >
                <Trash2 className="size-4 mr-2" />
                {cancelMut.isPending ? 'Cancelling…' : 'Cancel invitation'}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
